import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons';
import { Box, Flex, useMantineTheme } from '@mantine/core';
import { runInAction } from 'mobx';
import { observer, useLocalObservable } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { SegmentExport, TSegmentExportState } from '@feathr/blackbox';
import { Button, DebouncedInput, EmptyState, Icon, Input, TanStackTable } from '@feathr/components';
import Page from '@feathr/extender/App/Page';
import type { TCollectionTableSort } from '@feathr/extender/hooks';
import { useCollectionTable } from '@feathr/extender/hooks';
import { useLocalUrl, useStore } from '@feathr/extender/state';

import dataExportsColumns from './dataExportsColumns';
import { useExportsPolling } from './DataExportsPage.hooks';
import type { IExportsFilters, TExportsDisplayStatus } from './ExportsFilters';
import ExportsFiltersPopover from './ExportsFiltersPopover';

/** The states the page keeps polling for. */
const UNFINISHED_STATES = new Set<TSegmentExportState>(['pending', 'exporting', 'stopping']);
/*
 * Must be one of the sizes the pagination dropdown offers (10/20/50/100) — it selects the current
 * value by matching it against that list, so any other number leaves the dropdown blank.
 */
const DEFAULT_PAGE_SIZE = 10;
/** Newest export first until the user sorts on another column. */
const INITIAL_SORT: TCollectionTableSort = [{ desc: true, id: 'date_created' }];

/*
 * Maps the display-level status pill values to the raw BE state machine. "In progress" fans out to
 * `pending`, `exporting` and `stopping` because the chip merges them.
 *
 * "Completed" carries `expired` alongside `ready` because anhinga never stores `expired` — it
 * derives it when serializing a `ready` document whose file has outlived the retention window.
 * A document still reads `ready`, so the real backend already returns expired rows under this
 * filter and `state__in=expired` on its own would match nothing. Naming it keeps the same filter
 * working against payload-shaped fixtures, which store the serialized value. For the same reason
 * there is deliberately no "Expired" option in the pill list: it could never be queried.
 */
export const DISPLAY_TO_BE_STATES: Record<TExportsDisplayStatus, TSegmentExportState[]> = {
  in_progress: ['pending', 'exporting', 'stopping'],
  completed: ['ready', 'expired'],
  failed: ['failed'],
  cancelled: ['stopped'],
};

function DataExportsPage(): JSX.Element {
  const { SegmentExports } = useStore();
  const { t } = useTranslation();
  const localUrl = useLocalUrl();
  const theme = useMantineTheme();

  const filterState = useLocalObservable(() => ({
    appliedFilters: {} as IExportsFilters,
    /*
     * Held apart from `appliedFilters`, which the popover owns and replaces wholesale on every
     * apply. Merging the two would drop the search term whenever the user applied a filter.
     */
    search: '',
  }));

  function handleApplyFilters(newFilters: IExportsFilters): void {
    runInAction(() => {
      filterState.appliedFilters = { ...newFilters };
    });
  }

  /*
   * Stable identity on purpose. `DebouncedInput` passes this to `useDebouncedCallback`, which
   * lists it as a dependency and cancels any pending trailing call when it changes — so a handler
   * re-created each render can lose the call that carries the user's last keystroke.
   */
  const handleSearchChange = useCallback(
    (newValue?: string): void => {
      /*
       * Stored exactly as typed. `DebouncedInput` mirrors this back into the box through its
       * `defaultValue`, so trimming here would delete a space the moment the debounce fired:
       * "Annual " + pause + "Donors" would go out as "AnnualDonors". The trim belongs at the read
       * below, where the term becomes a filter.
       */
      runInAction(() => {
        filterState.search = newValue ?? '';
      });
    },
    [filterState],
  );

  const beStates = filterState.appliedFilters.status__in?.flatMap(
    (status) => DISPLAY_TO_BE_STATES[status],
  );

  /** The term the backend gets. Whitespace on its own is no search at all. */
  const searchTerm = filterState.search.trim();

  const tableFilters = {
    ...(beStates?.length ? { state__in: beStates } : {}),
    ...(filterState.appliedFilters.export_type__in?.length
      ? { export_type__in: filterState.appliedFilters.export_type__in }
      : {}),
    /*
     * `name` is a stored field on `PersonSegmentExport`, so `parse_field_filters` hands
     * `name__icontains` to mongoengine unchanged. Omitted while empty: the list route skips an
     * empty `icontains` anyway, and sending the key would still count as a changed filter and
     * bounce the table back to page one.
     */
    ...(searchTerm ? { name__icontains: searchTerm } : {}),
  };

  const hasAppliedFilters =
    !!filterState.appliedFilters.status__in?.length ||
    !!filterState.appliedFilters.export_type__in?.length ||
    !!searchTerm;

  /*
   * One list request drives the whole page. The table owns sort, page, and page size, and the rows
   * it returns are the same ones the empty-state and polling checks read — so there is no second,
   * unpaginated `list()` call to keep in step with it.
   */
  const tableProps = useCollectionTable<SegmentExport>(SegmentExports, {
    defaultParams: { filters: tableFilters },
    initialPageSize: DEFAULT_PAGE_SIZE,
    initialSort: INITIAL_SORT,
  });

  // TanStack re-measures column widths when the array identity changes, so build it once per `t`.
  const columns = useMemo(() => dataExportsColumns([], t), [t]);

  /*
   * Read off the whole result set, not the rows on screen. `pages` is the backend's
   * `ceil(count / page_size)`, so it reaches 0 only when nothing matches. Reading `items` instead
   * would mistake an emptied later page — archive the last row on page 2 — for an empty account and
   * swap the table for the CTA, taking with it the pagination the user needs to get back to page 1.
   */
  const hasExports = tableProps.pages > 0;
  const hasActiveExports = tableProps.items.some((row) => UNFINISHED_STATES.has(row.get('state')));

  useExportsPolling(SegmentExports, hasActiveExports);

  const showEmptyState = !hasExports && !tableProps.isLoading && !hasAppliedFilters;
  const showFilteredEmptyState = !hasExports && !tableProps.isLoading && hasAppliedFilters;

  return (
    <Page
      collapseHeader={false}
      collapseHeaderOnScroll={true}
      description={t(
        'View export progress, download completed files, and manage past exports. Exports are started from the Groups page.',
      )}
      title={t('Exports')}
    >
      {showEmptyState ? (
        <EmptyState
          description={t('Export one to get started.')}
          height={'full'}
          label={t('No exports found.')}
          name={'data-exports-empty-state'}
          theme={'white'}
          width={'full'}
        >
          <Button link={localUrl('/data/segments')} type={'primary'}>
            {t('Go to Groups')}
          </Button>
        </EmptyState>
      ) : (
        <>
          <Flex align={'center'} gap={theme.spacing['3']} justify={'space-between'} mb={'3'}>
            <Box w={256}>
              <DebouncedInput<string>
                defaultValue={filterState.search}
                onChange={handleSearchChange}
              >
                {(liveValue, onChangeLiveValue): JSX.Element => (
                  <Input
                    id={'exportsSearchInput'}
                    isClearable={true}
                    name={'exports-search'}
                    onChange={onChangeLiveValue}
                    placeholder={t('Search by name...')}
                    prefix={<Icon icon={faMagnifyingGlass} />}
                    type={'text'}
                    value={liveValue}
                  />
                )}
              </DebouncedInput>
            </Box>
            <ExportsFiltersPopover
              filters={filterState.appliedFilters}
              onApply={handleApplyFilters}
            />
          </Flex>
          {showFilteredEmptyState ? (
            <EmptyState
              description={t('Try adjusting your search or filter settings.')}
              height={'full'}
              label={t('No results found.')}
              name={'data-exports-filtered-empty-state'}
              theme={'white'}
              width={'full'}
            />
          ) : (
            <TanStackTable<SegmentExport>
              {...tableProps}
              columns={columns}
              noDataText={t('No exports found.')}
            />
          )}
        </>
      )}
    </Page>
  );
}

export default observer(DataExportsPage);
